import { useState, useEffect } from "react";
import ModalUsados from "../../components/Modal";
import ContactFloating from "../../components/contact-floating";
import './ZeroKM.styles.scss';

const ZeroKM = () => {
    const [vehicles, setVehicles] = useState([]);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [brandFilter, setBrandFilter] = useState('all');
    const [priceFilter, setPriceFilter] = useState('all');

    useEffect(() => {
        // Scroll to top when component mounts
        window.scrollTo(0, 0);
        fetchVehicles();
    }, []);

    const fetchVehicles = async () => {
        try {
            setLoading(true);
            const res = await fetch('https://dindorf.vercel.app/api/vehicles', {
                cache: 'no-store',
            });
            if (!res.ok) throw new Error('Error al obtener los vehículos');
            const data = await res.json();
            
            // Filtrar solo vehículos 0KM (año = 0, kilometraje = 0, y no motos)
            const zeroKmVehicles = data.filter(
                ({ año, kilometraje, tipoVehiculo }) =>
                    año === 0 && 
                    kilometraje === 0 && 
                    !tipoVehiculo.includes('moto')
            );
            
            setVehicles(zeroKmVehicles);
        } catch (error) {
            console.error('Error cargando los vehículos:', error);
        } finally {
            setLoading(false);
        }
    };

    const openModal = (vehicle) => {
        if (vehicle && Object.keys(vehicle).length > 0) {
            setSelectedVehicle(vehicle);
            setIsModalOpen(true);
        }
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedVehicle(null);
    };

    // Get unique brands for filter
    const uniqueBrands = [...new Set(vehicles.map(vehicle => vehicle.marca))].sort();

    // Filter vehicles based on search and filters
    const filteredVehicles = vehicles.filter(vehicle => {
        const matchesSearch = 
            vehicle.marca.toLowerCase().includes(searchTerm.toLowerCase()) ||
            vehicle.modelo.toLowerCase().includes(searchTerm.toLowerCase()) ||
            vehicle.variante?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesBrand = brandFilter === 'all' || vehicle.marca === brandFilter;
        
        const matchesPrice = priceFilter === 'all' || (() => {
            const price = vehicle.precioVenta;
            switch(priceFilter) {
                case 'under15': return price < 15000000;
                case '15to25': return price >= 15000000 && price < 25000000;
                case '25to40': return price >= 25000000 && price < 40000000;
                case 'over40': return price >= 40000000;
                default: return true;
            }
        })();

        return matchesSearch && matchesBrand && matchesPrice;
    });

    return (
        <div className="zerokm-page">
            <section className="zerokm-hero section animate__animated animate__fadeIn">
                <div className="container zerokm-container">
                    <h1 className="section-title">Vehículos 0KM</h1>
                    
                    <div className="page-intro">
                        <p className="intro-text">
                            Descubre nuestra exclusiva selección de vehículos 0 kilómetro. 
                            Todos nuestros autos nuevos vienen con garantía oficial de fábrica 
                            y el respaldo de nuestra experiencia de más de 60 años en el sector.
                        </p>
                        <div className="intro-features">
                            <div className="feature-tag">
                                <i className="ri-shield-check-line"></i>
                                <span>Garantía Oficial</span>
                            </div>
                            <div className="feature-tag">
                                <i className="ri-medal-line"></i>
                                <span>Estado Impecable</span>
                            </div>
                            <div className="feature-tag">
                                <i className="ri-service-line"></i>
                                <span>Servicio Post-Venta</span>
                            </div>
                        </div>
                    </div>

                    {/* Filtros */}
                    <div className="filters-section">
                        <div className="search-bar">
                            <input
                                type="text"
                                placeholder="Buscar por marca, modelo o versión..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                            <i className="ri-search-line search-icon"></i>
                        </div>

                        <div className="filter-group">
                            <select
                                value={brandFilter}
                                onChange={(e) => setBrandFilter(e.target.value)}
                                className="filter-select"
                            >
                                <option value="all">Todas las marcas</option>
                                {uniqueBrands.map(brand => (
                                    <option key={brand} value={brand}>{brand}</option>
                                ))}
                            </select>

                            <select
                                value={priceFilter}
                                onChange={(e) => setPriceFilter(e.target.value)}
                                className="filter-select"
                            >
                                <option value="all">Todos los precios</option>
                                <option value="under15">Hasta $15.000.000</option>
                                <option value="15to25">$15.000.000 - $25.000.000</option>
                                <option value="25to40">$25.000.000 - $40.000.000</option>
                                <option value="over40">Más de $40.000.000</option>
                            </select>
                        </div>
                    </div>

                    {/* Resultados */}
                    <div className="results-info">
                        <span className="results-count">
                            {filteredVehicles.length} vehículo{filteredVehicles.length !== 1 ? 's' : ''} 0KM disponible{filteredVehicles.length !== 1 ? 's' : ''}
                        </span>
                    </div>

                    {/* Grid de vehículos */}
                    <div className="vehicles-content">
                        {loading ? (
                            <div className="loading-container">
                                <div className="loading-spinner"></div>
                                <p>Cargando vehículos 0KM...</p>
                            </div>
                        ) : filteredVehicles.length > 0 ? (
                            <div className="vehicles-grid">
                                {filteredVehicles.map((vehicle) => (
                                    <div key={vehicle._id} className="vehicle-card">
                                        <div className="vehicle-image">
                                            <img
                                                src={vehicle.fotos?.[0] || 'https://via.placeholder.com/300'}
                                                alt={`${vehicle.marca} ${vehicle.modelo}`}
                                                loading="lazy"
                                            />
                                            <div className="zero-km-badge">
                                                0 KM
                                            </div>
                                            <div className="new-badge">
                                                NUEVO
                                            </div>
                                        </div>

                                        <div className="vehicle-info">
                                            <h3 className="vehicle-title">
                                                {vehicle.marca} {vehicle.modelo}
                                            </h3>
                                            <p className="vehicle-variant">
                                                {vehicle.variante}
                                            </p>
                                            
                                            <div className="vehicle-highlights">
                                                <div className="highlight-item">
                                                    <i className="ri-star-fill"></i>
                                                    <span>Vehículo Nuevo</span>
                                                </div>
                                                <div className="highlight-item">
                                                    <i className="ri-shield-check-fill"></i>
                                                    <span>Garantía de Fábrica</span>
                                                </div>
                                                <div className="highlight-item">
                                                    <i className="ri-service-fill"></i>
                                                    <span>Service Incluido</span>
                                                </div>
                                            </div>

                                            <div className="vehicle-price">
                                                <span className="price-label">Precio Final</span>
                                                <span className="price-value">
                                                    $
                                                    {vehicle.precioVenta
                                                        ? vehicle.precioVenta.toLocaleString('es-ES')
                                                        : 'Consultar'
                                                    }
                                                </span>
                                            </div>

                                            <div className="action-buttons">
                                                <button
                                                    className="info-button"
                                                    onClick={() => openModal(vehicle)}
                                                >
                                                    <i className="ri-information-line"></i>
                                                    Ver Detalles
                                                </button>
                                                <button
                                                    className="whatsapp-button"
                                                    onClick={() =>
                                                        window.open(
                                                            `https://wa.me/5492634588256?text=Hola!%20Estoy%20interesado%20en%20el%20${encodeURIComponent(vehicle.marca)}%20${encodeURIComponent(vehicle.modelo)}%200KM`,
                                                            '_blank'
                                                        )
                                                    }
                                                >
                                                    <i className="ri-whatsapp-line"></i>
                                                    Consultar
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="no-results">
                                <i className="ri-car-line no-results-icon"></i>
                                <h3>No hay vehículos 0KM disponibles</h3>
                                <p>En este momento no tenemos vehículos que coincidan con tu búsqueda</p>
                            </div>
                        )}
                    </div>

                    {/* Información adicional */}
                    <div className="info-section">
                        <h2 className="info-title">¿Por qué elegir un vehículo 0KM con nosotros?</h2>
                        <div className="benefits-grid">
                            <div className="benefit-card">
                                <div className="benefit-icon">
                                    <i className="ri-award-line"></i>
                                </div>
                                <h3>Garantía Oficial</h3>
                                <p>Todos nuestros vehículos 0KM incluyen garantía completa de fábrica</p>
                            </div>
                            
                            <div className="benefit-card">
                                <div className="benefit-icon">
                                    <i className="ri-money-dollar-circle-line"></i>
                                </div>
                                <h3>Financiación</h3>
                                <p>Planes de financiación adaptados a tus necesidades y posibilidades</p>
                            </div>
                            
                            <div className="benefit-card">
                                <div className="benefit-icon">
                                    <i className="ri-tools-line"></i>
                                </div>
                                <h3>Service Oficial</h3>
                                <p>Mantenimiento y reparaciones con repuestos originales</p>
                            </div>
                            
                            <div className="benefit-card">
                                <div className="benefit-icon">
                                    <i className="ri-user-heart-line"></i>
                                </div>
                                <h3>Asesoramiento</h3>
                                <p>Te ayudamos a elegir el vehículo perfecto para tus necesidades</p>
                            </div>
                        </div>
                    </div>

                    {/* Call to action */}
                    <div className="cta-section">
                        <h2>¿Interesado en algún vehículo 0KM?</h2>
                        <p>Contáctanos para más información sobre disponibilidad, financiación y entrega</p>
                        <button
                            className="cta-button"
                            onClick={() =>
                                window.open(
                                    'https://wa.me/5492634588256?text=Hola,%20estoy%20interesado%20en%20vehículos%200KM.%20¿Podrían%20brindarme%20más%20información?',
                                    '_blank'
                                )
                            }
                        >
                            <i className="ri-whatsapp-line"></i>
                            Consultar Disponibilidad
                        </button>
                    </div>
                </div>
            </section>

            <ModalUsados
                isOpen={isModalOpen}
                onClose={closeModal}
                selectedItem={selectedVehicle}
            />

            <ContactFloating />
        </div>
    );
};

export default ZeroKM;